/*
 * FLIPSIDE EXPERIENCE ENGINE
 * Optimized for performance
 */

(function() {
  'use strict';

  // ============================================
  // DEVICE DETECTION (run once)
  // ============================================
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============================================
  // SCROLL PROGRESS BAR
  // ============================================
  class ScrollProgress {
    constructor() {
      this.bar = null;
      this.ticking = false;
      this.init();
    }

    init() {
      this.bar = document.createElement('div');
      this.bar.className = 'fl-scroll-progress';
      document.body.appendChild(this.bar);

      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });
    }

    update() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      this.bar.style.width = (scrollTop / docHeight) * 100 + '%';
    }
  }

  // ============================================
  // MAGNETIC BUTTONS (Desktop only)
  // ============================================
  class MagneticButtons {
    constructor() {
      if (isMobile || prefersReducedMotion) return;
      this.buttons = document.querySelectorAll('[data-magnetic]');
      this.buttons.forEach(btn => {
        btn.addEventListener('mousemove', (e) => {
          const rect = btn.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;
          btn.style.transform = `translate(${x * 0.2}px, ${y * 0.2}px)`;
        });
        btn.addEventListener('mouseleave', () => btn.style.transform = '');
      });
    }
  }

  // ============================================
  // 3D CARD TILT (Desktop only)
  // ============================================
  class TiltCards {
    constructor() {
      if (isMobile || prefersReducedMotion) return;
      this.cards = document.querySelectorAll('[data-tilt]');
      this.cards.forEach(card => {
        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const rotateX = (y - rect.height / 2) / 15;
          const rotateY = (rect.width / 2 - x) / 15;
          card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
        });
        card.addEventListener('mouseleave', () => card.style.transform = '');
      });
    }
  }

  // ============================================
  // SCROLL ANIMATIONS (IntersectionObserver)
  // ============================================
  class ScrollAnimations {
    constructor() {
      if (prefersReducedMotion) return;
      const elements = document.querySelectorAll('.fade-up, .scale-in, .slide-left, .slide-right, [data-reveal="true"]');
      if (!elements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

      elements.forEach(el => observer.observe(el));
    }
  }

  // ============================================
  // PARALLAX (Desktop only, throttled)
  // ============================================
  class Parallax {
    constructor() {
      if (isMobile || prefersReducedMotion) return;
      this.elements = document.querySelectorAll('[data-parallax]');
      if (!this.elements.length) return;
      
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrollY = window.scrollY;
            this.elements.forEach(el => {
              el.style.transform = `translateY(${scrollY * (parseFloat(el.dataset.parallax) || 0.3)}px)`;
            });
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }
  }

  // ============================================
  // MICRO-INTERACTIONS (Button ripple)
  // ============================================
  class MicroInteractions {
    constructor() {
      if (prefersReducedMotion) return;
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.btn');
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'btn-ripple';
        ripple.style.left = (e.clientX - rect.left) + 'px';
        ripple.style.top = (e.clientY - rect.top) + 'px';
        btn.appendChild(ripple);
        setTimeout(() => ripple.remove(), 600);
      });
    }
  }

  // ============================================
  // EASTER EGG
  // ============================================
  class EasterEgg {
    constructor() {
      const code = [38,38,40,40,37,39,37,39,66,65];
      let current = 0;
      document.addEventListener('keydown', (e) => {
        if (e.keyCode === code[current]) {
          current++;
          if (current === code.length) {
            localStorage.setItem('fl_operator_status', 'true');
            const m = document.createElement('div');
            m.className = 'easter-egg-modal';
            m.innerHTML = '<div class="easter-egg-content"><div class="easter-egg-glitch">ACCESS GRANTED</div><p>You found the secret.</p><div class="easter-egg-code">OPERATOR STATUS</div><p style="font-size:0.75rem;color:var(--muted)">Early access to all future drops.</p><button class="btn" onclick="this.closest(\'.easter-egg-modal\').remove()">OK</button></div>';
            document.body.appendChild(m);
            current = 0;
          }
        } else current = 0;
      });
    }
  }

  // ============================================
  // LAZY IMAGES
  // ============================================
  class LazyImages {
    constructor() {
      document.querySelectorAll('img:not([loading])').forEach(img => {
        if (img.getBoundingClientRect().top > window.innerHeight) {
          img.loading = 'lazy';
        }
      });
    }
  }

  // ============================================
  // USA 250 CACHE-SAFE PURCHASE PATCH
  // ============================================
  function patchUSA250BundleFlow() {
    const isUSA250Page = /^\/products\/usa-250-snapback(?:-live)?(?:\/)?$/.test(window.location.pathname);
    if (!isUSA250Page || document.documentElement.dataset.usa250BundlePatched === 'true') return;

    const buySection = document.querySelector('[id^="usa250-buy-"]');
    const productForm = document.querySelector('.usa250-buy-form');
    if (!buySection || !productForm) return;

    document.documentElement.dataset.usa250BundlePatched = 'true';

    const style = document.createElement('style');
    style.textContent = `
      .usa250 .usa250-bundle-offer {
        position: relative;
        margin: 18px 0 16px;
        padding: 16px;
        border: 1px solid rgba(204, 165, 94, 0.34);
        background:
          linear-gradient(135deg, rgba(204, 165, 94, 0.12), rgba(10, 10, 10, 0.72)),
          rgba(12, 12, 12, 0.72);
        box-shadow: inset 0 1px 0 rgba(255, 236, 190, 0.08), 0 14px 35px rgba(0, 0, 0, 0.28);
      }
      .usa250 .usa250-bundle-offer__title {
        margin: 0 0 12px;
        color: #f1d39a;
        font-size: 0.72rem;
        font-weight: 900;
        letter-spacing: 0.22em;
        text-transform: uppercase;
      }
      .usa250 .usa250-bundle-offer__row {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 9px 0;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        color: rgba(250, 244, 230, 0.76);
        letter-spacing: 0.04em;
      }
      .usa250 .usa250-bundle-offer__row strong {
        color: #fff1bd;
        text-align: right;
      }
      .usa250 .usa250-bundle-offer__note,
      .usa250 .usa250-sms-access__note {
        margin: 12px 0 0;
        color: rgba(250, 244, 230, 0.58);
        font-size: 0.76rem;
        line-height: 1.45;
      }
      .usa250 .usa250-purchase-controls {
        display: grid;
        grid-template-columns: minmax(104px, 0.42fr) minmax(180px, 1fr);
        gap: 12px;
        align-items: stretch;
        margin-bottom: 12px;
      }
      .usa250 .usa250-quantity-field {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 7px;
        min-height: 58px;
        padding: 9px 12px;
        border: 1px solid rgba(204, 165, 94, 0.28);
        background: rgba(0, 0, 0, 0.36);
      }
      .usa250 .usa250-quantity-label {
        color: rgba(250, 244, 230, 0.62);
        font-size: 0.64rem;
        font-weight: 900;
        letter-spacing: 0.17em;
        text-transform: uppercase;
      }
      .usa250 .usa250-quantity-input {
        width: 100%;
        padding: 0;
        border: 0;
        background: transparent;
        color: #fff5d4;
        font-size: 1.35rem;
        font-weight: 900;
        line-height: 1;
      }
      .usa250 .usa250-react-sticky__bundle {
        margin-top: 2px;
        color: rgba(241, 211, 154, 0.72);
        font-size: 0.66rem;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      @media (max-width: 780px) {
        .usa250 .usa250-purchase-controls {
          grid-template-columns: 0.52fr 1fr;
          gap: 9px;
        }
        .usa250 .usa250-bundle-offer {
          margin-top: 16px;
          padding: 14px;
        }
      }
    `;
    document.head.appendChild(style);

    const remainingText = document.querySelector('[data-usa250-remaining-display]')?.textContent || '';
    const remaining = parseInt(remainingText.replace(/[^\d]/g, ''), 10);
    const legacyQuantity = productForm.querySelector('input[name="quantity"][type="hidden"]');
    if (legacyQuantity) {
      legacyQuantity.dataset.usa250LegacyQuantity = 'true';
      legacyQuantity.removeAttribute('name');
    }

    let quantityInput = productForm.querySelector('[data-usa250-quantity]');
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.className = 'usa250-input usa250-quantity-input';
      quantityInput.id = 'usa250-quantity-patched';
      quantityInput.type = 'number';
      quantityInput.name = 'quantity';
      quantityInput.value = legacyQuantity?.value || '1';
      quantityInput.min = '1';
      quantityInput.inputMode = 'numeric';
      quantityInput.dataset.usa250Quantity = 'true';
      if (Number.isFinite(remaining) && remaining > 0) quantityInput.max = String(remaining);

      const label = document.createElement('label');
      label.className = 'usa250-quantity-field';
      label.setAttribute('for', quantityInput.id);
      label.innerHTML = '<span class="usa250-quantity-label">Quantity</span>';
      label.appendChild(quantityInput);

      const submit = productForm.querySelector('[data-usa250-add], button[type="submit"]');
      const controls = document.createElement('div');
      controls.className = 'usa250-purchase-controls usa250-purchase-controls--patched';
      controls.appendChild(label);
      if (submit) {
        submit.removeAttribute('data-usa250-claim');
        productForm.insertBefore(controls, submit);
        controls.appendChild(submit);
      } else {
        productForm.insertBefore(controls, productForm.firstChild);
      }
    }

    if (!buySection.querySelector('.usa250-bundle-offer')) {
      const bundle = document.createElement('div');
      bundle.className = 'usa250-bundle-offer';
      bundle.setAttribute('aria-label', 'USA 250 pricing');
      bundle.innerHTML = [
        '<p class="usa250-bundle-offer__title">Bundle pricing is live</p>',
        '<div class="usa250-bundle-offer__row"><span>1 Hat</span><strong>$49.97</strong></div>',
        '<div class="usa250-bundle-offer__row"><span>2 Hats</span><strong>$90 · Save $9.94</strong></div>',
        '<div class="usa250-bundle-offer__row"><span>3 Hats</span><strong>$115 · Save $34.91</strong></div>',
        '<p class="usa250-bundle-offer__note">Automatic bundle savings apply at checkout. Free shipping is included.</p>'
      ].join('');
      productForm.parentElement?.insertBefore(bundle, productForm);
    }

    document.querySelectorAll('.usa250-form-note').forEach((note) => {
      if (/Quantity locked|Bundle pricing/i.test(note.textContent || '')) {
        note.textContent = 'Choose your quantity. Automatic bundle savings and free shipping apply at checkout.';
      }
    });

    document.querySelectorAll('.usa250-sms-line').forEach((line) => {
      line.innerHTML = 'Text <strong>FLIPSIDE</strong> to <a href="sms:+18337162637?&body=FLIPSIDE">(833) 716-2637</a> for drop alerts and early access on next releases.';
      if (line.classList.contains('usa250-sms-fallback')) {
        line.insertAdjacentText('beforeend', ' $49.97 single. 2 for $90. 3 for $115. Free shipping.');
      }
    });

    const smsSlot = document.querySelector('[data-usa250-react-sms-root]');
    const joinSection = document.querySelector('[id^="usa250-join-"]');
    if (smsSlot && !smsSlot.id) smsSlot.id = 'usa250-text-access-patched';
    const smsTarget = smsSlot || joinSection;
    document.querySelectorAll('.usa250-link-button--ghost').forEach((link) => {
      const label = (link.textContent || '').trim();
      const href = link.getAttribute('href') || '';
      if (/see the numbering/i.test(label) || href.indexOf('usa250-proof') >= 0) {
        link.textContent = 'Drop alerts';
        if (smsTarget?.id) link.setAttribute('href', `#${smsTarget.id}`);
        link.setAttribute('data-usa250-sms-jump', '');
      }
    });
    document.querySelectorAll('a[href*="AMERICANA"]').forEach((link) => {
      link.setAttribute('href', (link.getAttribute('href') || '').replace(/AMERICANA/g, 'FLIPSIDE'));
    });

    const smsBody = document.querySelector('.usa250-sms-access__body');
    if (smsBody) smsBody.textContent = 'Text access gets drop alerts and early access on next releases.';
    const smsDesktop = document.querySelector('.usa250-sms-access__desktop');
    if (smsDesktop) {
      smsDesktop.innerHTML = 'Text <strong>FLIPSIDE</strong> to (833) 716-2637 for drop alerts and early access on next releases.';
    }
    const smsCard = document.querySelector('.usa250-sms-access');
    if (smsCard && !smsCard.querySelector('.usa250-sms-access__note')) {
      const note = document.createElement('p');
      note.className = 'usa250-sms-access__note';
      note.textContent = '$49.97 single. 2 for $90. 3 for $115. Free shipping.';
      smsCard.appendChild(note);
    }

    const stickyCopy = document.querySelector('.usa250-react-sticky__copy');
    if (stickyCopy && !stickyCopy.querySelector('.usa250-react-sticky__bundle')) {
      const bundleLine = document.createElement('div');
      bundleLine.className = 'usa250-react-sticky__bundle';
      bundleLine.textContent = '$49.97 single. 2 for $90. 3 for $115. Free shipping.';
      stickyCopy.appendChild(bundleLine);
    }
    document.querySelectorAll('.usa250-react-sticky__sms, .usa250-sms-access__button').forEach((button) => {
      button.textContent = 'TEXT ACCESS - DROP ALERTS';
      button.setAttribute('href', 'sms:+18337162637?&body=FLIPSIDE');
    });
    document.querySelectorAll('.usa250-express, .usa250 .shopify-payment-button, .usa250 shopify-accelerated-checkout').forEach((node) => {
      node.hidden = true;
      node.style.display = 'none';
    });

    const focusPurchase = () => {
      buySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      buySection.classList.add('is-focused');
      setTimeout(() => buySection.classList.remove('is-focused'), 900);
      setTimeout(() => quantityInput?.focus({ preventScroll: true }), 360);
    };

    const addProductToCart = (source) => {
      const submit = productForm.querySelector('[data-usa250-add], button[type="submit"]');
      const variantInput = productForm.querySelector('[name="id"]');
      const variantId = window.FLIPSIDE_USA250_VARIANT_ID || variantInput?.value || '41071255846984';
      if (productForm.dataset.usa250Carting === 'true') return;
      if (!variantId) {
        focusPurchase();
        return;
      }

      let quantity = Number.parseInt(quantityInput?.value || productForm.querySelector('input[name="quantity"]')?.value || '1', 10);
      if (!quantity || quantity < 1) quantity = 1;

      productForm.dataset.usa250Carting = 'true';
      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.disabled = true;
      }

      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(quantity));
      productForm.querySelectorAll('input[name^="properties["]').forEach((input) => {
        if (input.name) formData.append(input.name, input.value || '');
      });

      const releaseButton = () => {
          productForm.dataset.usa250Carting = 'false';
          if (submit) {
            submit.removeAttribute('aria-busy');
            submit.disabled = false;
          }
      };

      const fallbackToCartAdd = () => {
          if (window.FLIPSIDE_USA250_CART_FALLBACK) {
            window.FLIPSIDE_USA250_CART_FALLBACK();
            return;
          }
          window.location.href = `/cart/add?id=${encodeURIComponent(String(variantId))}&quantity=${encodeURIComponent(String(quantity))}&return_to=/cart`;
      };

      const prepareUSA250Cart = () => fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          const items = cart && Array.isArray(cart.items) ? cart.items : [];
          const hasVariant = items.some((item) => String(item.variant_id) === String(variantId));
          const staleUSA250 = items.filter((item) => {
            const label = `${item.handle || ''} ${item.product_title || ''} ${item.title || ''}`;
            return /usa\s*250/i.test(label) && String(item.variant_id) !== String(variantId) && item.key;
          });
          if (!staleUSA250.length) return hasVariant;
          return Promise.all(staleUSA250.map((item) => fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          }).catch(() => {}))).then(() => hasVariant);
        })
        .catch(() => false);

      const postAdd = () => {
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
          body: formData
        })
          .then((response) => {
            if (response.ok) return response.json();
            return response.json().catch(() => ({})).then((payload) => {
              throw new Error(payload.description || payload.message || 'Unable to add USA 250 to cart.');
            });
          })
          .then(() => {
            window.location.href = '/cart';
          })
          .catch(() => {
            prepareUSA250Cart().then((hasVariant) => {
              if (hasVariant) {
                window.location.href = '/cart';
                return;
              }
              releaseButton();
              fallbackToCartAdd();
            });
          });
      };

      prepareUSA250Cart().then((hasVariant) => {
        if (hasVariant) {
          window.location.href = '/cart';
          return;
        }
        postAdd();
      });
    };

    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-usa250-claim], .usa250-react-sticky__button');
      if (!target) return;

      const isProductSubmit = productForm.contains(target) && target.matches('button[type="submit"], input[type="submit"]');
      if (isProductSubmit) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addProductToCart(target);
    }, true);

    productForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const legacy = productForm.querySelector('[data-usa250-legacy-quantity]');
      if (legacy) legacy.removeAttribute('name');
      const quantity = productForm.querySelector('[data-usa250-quantity]');
      if (quantity && (!quantity.value || Number(quantity.value) < 1)) quantity.value = '1';
      addProductToCart(event.submitter);
    }, true);
  }

  // ============================================
  // INIT
  // ============================================
  function init() {
    patchUSA250BundleFlow();
    new ScrollProgress();
    new LazyImages();
    if (!isMobile && !prefersReducedMotion) {
      new MagneticButtons();
      new TiltCards();
      new Parallax();
    }
    if (!prefersReducedMotion) {
      new ScrollAnimations();
      new MicroInteractions();
    }
    new EasterEgg();
    document.body.classList.add('fl-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
